import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { useAuth } from '../../contexts/AuthContext';
import { Plus, Edit, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminOffers = () => {
  const { getAuthHeader } = useAuth();

  const [offers, setOffers] = useState([]);
  const [products, setProducts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    product_id: '',
    start_date: '',
    end_date: '',
    is_active: true,
  });

  useEffect(() => {
    loadOffers();
    loadProducts();
  }, []);

  const loadOffers = async () => {
    try {
      const response = await axios.get(`${API_URL}/offers`, {
        headers: getAuthHeader(),
      });
      setOffers(response.data);
    } catch (error) {
      console.error('Error loading offers:', error);
      toast.error('Erro ao carregar ofertas');
    }
  };

  const loadProducts = async () => {
    try {
      const response = await axios.get(`${API_URL}/products?limit=2000`, {
        headers: getAuthHeader(),
      });
      setProducts(response.data);
    } catch (error) {
      console.error('Error loading products:', error);
      toast.error('Erro ao carregar produtos');
    }
  };

  const getProductById = (productId) => {
    return products.find((product) => product.id === productId);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        start_date: new Date(formData.start_date).toISOString(),
        end_date: new Date(formData.end_date).toISOString(),
      };

      if (editingOffer) {
        await axios.put(`${API_URL}/offers/${editingOffer.id}`, payload, {
          headers: getAuthHeader(),
        });
        toast.success('Oferta atualizada com sucesso!');
      } else {
        await axios.post(`${API_URL}/offers`, payload, {
          headers: getAuthHeader(),
        });
        toast.success('Oferta criada com sucesso!');
      }

      setShowModal(false);
      resetForm();
      loadOffers();
    } catch (error) {
      console.error('Error saving offer:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar oferta');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (offer) => {
    setEditingOffer(offer);
    setFormData({
      title: offer.title,
      product_id: offer.product_id || '',
      start_date: new Date(offer.start_date).toISOString().split('T')[0],
      end_date: new Date(offer.end_date).toISOString().split('T')[0],
      is_active: offer.is_active,
    });
    setShowModal(true);
  };

  const handleDelete = async (offerId) => {
    if (!window.confirm('Tem certeza que deseja deletar esta oferta?')) return;

    try {
      await axios.delete(`${API_URL}/offers/${offerId}`, {
        headers: getAuthHeader(),
      });
      toast.success('Oferta deletada com sucesso!');
      loadOffers();
    } catch (error) {
      console.error('Error deleting offer:', error);
      toast.error('Erro ao deletar oferta');
    }
  };

  const resetForm = () => {
    setEditingOffer(null);
    setFormData({
      title: '',
      product_id: '',
      start_date: '',
      end_date: '',
      is_active: true,
    });
  };

  return (
    <AdminLayout>
      <div>
        <div className="flex items-center justify-between mb-8">
          <h1
            className="text-3xl font-bold"
            style={{ color: '#273e82', fontFamily: 'Manrope' }}
          >
            Ofertas da Semana
          </h1>

          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="primary-button flex items-center gap-2"
            data-testid="add-offer-button"
          >
            <Plus size={20} />
            Nova Oferta
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {offers.length > 0 ? (
            offers.map((offer) => {
              const product = getProductById(offer.product_id);
              const mainImage =
                product?.images && product.images.length > 0
                  ? product.images[0].image_url
                  : 'https://images.unsplash.com/photo-1588345921523-c2dcdb7f1dcd?w=400';

              const displayPrice =
                product?.promo_price != null && product?.price != null && product.promo_price < product.price
                  ? product.promo_price
                  : product?.price;

              return (
                <div
                  key={offer.id}
                  className="bg-white rounded-xl border border-slate-200 overflow-hidden"
                  data-testid={`offer-card-${offer.id}`}
                >
                  <div className="h-[220px] bg-slate-100 overflow-hidden">
                    <img
                      src={mainImage}
                      alt={product?.name || offer.title}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  <div className="p-4">
                    <h3
                      className="font-bold text-lg mb-2"
                      style={{ color: '#273e82' }}
                    >
                      {offer.title}
                    </h3>

                    {product && (
                      <>
                        <p className="text-sm text-slate-600">
                          Produto: {product.name}
                        </p>

                        {product.reference && (
                          <p className="text-sm text-slate-500 mt-1">
                            Ref: {product.reference}
                          </p>
                        )}

                        <div className="mt-2">
                          {product.omit_price ? (
                            <p className="font-semibold text-slate-600 text-sm">
                              Adicione ao carrinho e consulte o preço
                            </p>
                          ) : displayPrice != null ? (
                            product.promo_price != null &&
                            product.price != null &&
                            product.promo_price < product.price ? (
                              <div>
                                <p className="line-through text-slate-400 text-sm">
                                  R$ {Number(product.price).toFixed(2)}
                                </p>
                                <p className="font-bold" style={{ color: '#273e82' }}>
                                  R$ {Number(product.promo_price).toFixed(2)}
                                </p>
                              </div>
                            ) : (
                              <p className="font-bold" style={{ color: '#273e82' }}>
                                R$ {Number(displayPrice).toFixed(2)}
                              </p>
                            )
                          ) : null}
                        </div>
                      </>
                    )}

                    <p className="text-sm text-slate-600 mt-3 mb-2">
                      {new Date(offer.start_date).toLocaleDateString('pt-BR')} até{' '}
                      {new Date(offer.end_date).toLocaleDateString('pt-BR')}
                    </p>

                    <span
                      className={`inline-block px-3 py-1 rounded-full text-xs font-bold mb-4 ${
                        offer.is_active
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {offer.is_active ? 'ATIVA' : 'INATIVA'}
                    </span>

                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(offer)}
                        className="flex-1 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors font-semibold"
                        data-testid={`edit-offer-${offer.id}`}
                      >
                        Editar
                      </button>

                      <button
                        onClick={() => handleDelete(offer.id)}
                        className="px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition-colors"
                        data-testid={`delete-offer-${offer.id}`}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="col-span-full bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-500">
              Nenhuma oferta cadastrada.
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="offer-modal"
          >
            <div className="sticky top-0 bg-white border-b border-slate-200 p-6 flex items-center justify-between">
              <h2
                className="text-2xl font-bold"
                style={{ color: '#273e82', fontFamily: 'Manrope' }}
              >
                {editingOffer ? 'Editar Oferta' : 'Nova Oferta'}
              </h2>

              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div>
                <label
                  className="block text-sm font-semibold mb-2"
                  style={{ color: '#273e82' }}
                >
                  Título *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                  className="input-field"
                  data-testid="offer-title-input"
                />
              </div>

              <div>
                <label
                  className="block text-sm font-semibold mb-2"
                  style={{ color: '#273e82' }}
                >
                  Produto *
                </label>
                <select
                  name="product_id"
                  value={formData.product_id}
                  onChange={handleInputChange}
                  required
                  className="input-field"
                  data-testid="offer-product-select"
                >
                  <option value="">Selecione um produto</option>
                  {products.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.name}
                      {product.reference ? ` - Ref: ${product.reference}` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label
                    className="block text-sm font-semibold mb-2"
                    style={{ color: '#273e82' }}
                  >
                    Data de Início *
                  </label>
                  <input
                    type="date"
                    name="start_date"
                    value={formData.start_date}
                    onChange={handleInputChange}
                    required
                    className="input-field"
                    data-testid="offer-start-date-input"
                  />
                </div>

                <div>
                  <label
                    className="block text-sm font-semibold mb-2"
                    style={{ color: '#273e82' }}
                  >
                    Data de Término *
                  </label>
                  <input
                    type="date"
                    name="end_date"
                    value={formData.end_date}
                    onChange={handleInputChange}
                    required
                    className="input-field"
                    data-testid="offer-end-date-input"
                  />
                </div>
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                  className="w-5 h-5"
                  data-testid="offer-active-checkbox"
                />
                <span
                  className="font-semibold"
                  style={{ color: '#273e82' }}
                >
                  Oferta Ativa
                </span>
              </label>

              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-6 py-3 border-2 border-slate-300 rounded-lg font-bold hover:bg-slate-50 transition-all"
                >
                  Cancelar
                </button>

                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 primary-button disabled:opacity-50"
                  data-testid="submit-offer-button"
                >
                  {loading ? 'Salvando...' : editingOffer ? 'Atualizar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminOffers;